import { tokens } from "lib/colors"
import Text from "lib/components/Text"
import Button from "lib/components/Button"
import { useContext, useState } from "react"
import { motion } from "framer-motion"
import { LanguageContext } from "context/LanguageContext"
import ContainerComponent from "lib/components/Container"
import { gtmEvent } from "lib/gtm"
import dynamic from "next/dynamic"
import { optimizeImageCloudinary } from "lib/helpers"
import ColoredText from "@components/shared-components/ColoredText"

const ImageComponent = dynamic(() => import("lib/components/Image"), {
  ssr: false,
})

const listData = [
  {
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1708919512/ngabuburit/automotive.webp",
    title: "Automotive",
    url: "/programs/automotive",
    detail_left: ["Risk Management", "Treasury", "BNSP Certification"],
    detail_right: ["General Banking", "Human Capital", "And 5+ more"],
  },
  {
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1708919512/ngabuburit/bank-finance.webp",
    title: "Bank & Finance",
    url: "/programs/banking",
    detail_left: ["Treasury", "Fintech", "Risk Management"],
    detail_right: ["Shariah Banking", "Credit Management", "And 5+ more"],
  },
  {
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1708919512/ngabuburit/cross-function.webp",
    title: "Cross-Function Skills",
    url: "/programs/general",
    detail_left: ["Finance, Tax & ac...", "Training Skills", "Office Management"],
    detail_right: ["Human Capital", "Legal", "And 5+ more"],
  },
  {
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1708919512/ngabuburit/fmcg.webp",
    title: "FMCG",
    url: "/programs/consumer-goods",
    detail_left: ["Food & Beverage"],
    detail_right: [],
  },
  {
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1708919520/ngabuburit/hospitality.webp",
    title: "Hospitality",
    url: "/programs/hospitality",
    detail_left: ["Risk Management", "Treasury", "BNSP Certification"],
    detail_right: ["General Banking", "Human Capital", "And 5+ more"],
  },
  {
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1708919520/ngabuburit/information-technology.webp",
    title: "Information System & Technology",
    url: "/programs/information-technology",
    detail_left: ["Digital Marketing", "Web & Mobile Development"],
    detail_right: ["Microsoft Office", "Cybersecurity", "Data"],
  },
  {
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1708919521/ngabuburit/insurance.webp",
    title: "Insurance",
    url: "/programs/insurance",
    detail_left: ["Risk Management", "Treasury", "BNSP Certification"],
    detail_right: ["General Banking", "Human Capital", "And 5+ more"],
  },
  {
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1708919521/ngabuburit/natural-resource.webp",
    title: "Natural & Resources",
    url: "/programs/natural-resources",
    detail_left: ["Mining", "Water"],
    detail_right: ["Oil & Gas"],
  },
  {
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1708919521/ngabuburit/telecomunication.webp",
    title: "Telecomunications",
    url: "/programs/telecommunication",
    detail_left: ["Risk Management", "Treasury", "BNSP Certification"],
    detail_right: ["General Banking", "Human Capital", "And 5+ more"],
  },
]

function Categories() {
  const { useTranslation: t, language } = useContext(LanguageContext)
  const [selectedHoverIndex, setSelectedHoverIndex] = useState(-1)

  return (
    <section className="px-4 lg:px-0 py-4 lg:py-[52px]">
      <ContainerComponent>
        <div className="flex flex-col lg:flex-row justify-between mb-6 lg:mb-8 gap-1">
          <div className="w-full flex flex-col items-center justify-center gap-1 lg:gap-2">
            <Text weight="semibold" customClass="text-center text-base lg:text-2xl">
              <ColoredText
                text={t("ngabuburit_category_section")}
                language={language}
                keywords={{
                  EN: ["categories"],
                  ID: ["kategori"],
                }}
                color={tokens.primary.DEFAULT}
              />
              {t("")}
            </Text>
            <Text customClass="text-center text-sm lg:text-lg">
              {t("ngabuburit_category_description")}
            </Text>
          </div>
        </div>
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-3 w-full flex-wrap gap-4 sm:gap-x-6 sm:gap-y-5">
            {listData.map(({ image, title, url, detail_left, detail_right }, indexListData) => (
              <div
                key={indexListData}
                className={`cursor-pointer ${
                  indexListData === listData.length - 1 ? "hidden sm:block" : "block"
                }`}>
                <div
                  onMouseOver={() => setSelectedHoverIndex(indexListData)}
                  onMouseLeave={() => setSelectedHoverIndex(-1)}
                  onFocus={() => setSelectedHoverIndex(indexListData)}>
                  <motion.div
                    className="relative rounded-xl bg-neutral-50 p-5 h-[81.2px] sm:h-[200px]"
                    key={indexListData}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      gtmEvent("cs_view_program", {
                        program_category: title,
                      })
                    }}
                    initial={{
                      scale: 0.98,
                    }}
                    whileHover={{
                      cursor: "pointer",
                      scale: 1,
                    }}
                    transition={{ duration: 0.5 }}>
                    <a href={url} aria-label={title}>
                      <div className="absolute inset-0 rounded-lg z-0">
                        <ImageComponent
                          src={optimizeImageCloudinary(image, "high", "w_768")}
                          alt={title}
                          rounded
                          roundedSize="extra-large"
                        />
                      </div>
                    </a>
                    {/* Overlay */}
                    {selectedHoverIndex === indexListData && (
                      <div className="absolute inset-0 bg-[#000000] bg-opacity-75 rounded-xl z-0" />
                    )}
                    <motion.div
                      className="w-full h-full flex flex-grow flex-col items-center justify-center"
                      initial={{
                        y: 5,
                      }}
                      whileHover={{
                        cursor: "pointer",
                        y: 0,
                      }}
                      transition={{ duration: 0.5 }}>
                      <a href={url} className="w-full">
                        <div className="relative z-10">
                          <Text
                            color="light"
                            alignText="center"
                            size="medium"
                            weight="bold"
                            customClass="leading-[16px] sm:leading[24px] text-[10px] sm:text-[20px]">
                            {title}
                          </Text>
                        </div>
                        <div className="w-full hidden sm:block">
                          {selectedHoverIndex === indexListData && (
                            <div className="w-full">
                              {detail_left.length > 0 ? (
                                <div className="grid grid-cols-2 gap-4 relative z-10 ml-4">
                                  <ul className="text-white">
                                    {detail_left.map((itemLeft) => (
                                      <li key={itemLeft}>
                                        <Text color="light">{itemLeft}</Text>
                                      </li>
                                    ))}
                                    {detail_left.length === 1 && <div className="h-12" />}
                                    {detail_right.length === 1 && <div className="h-6" />}
                                  </ul>
                                  <ul className="text-white">
                                    {detail_right.map((itemRight) => (
                                      <li key={itemRight}>
                                        <Text color="light">{itemRight}</Text>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : (
                                <div className="h-24" />
                              )}
                            </div>
                          )}
                        </div>
                      </a>
                      {selectedHoverIndex === indexListData && (
                        <div className="relative z-30 w-full hidden sm:block">
                          <Button
                            href="https://wa.me/6281312000375"
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            customClass="text-[8px] sm:text-sm h-5 sm:h-full"
                            variant="contained"
                            color="primary"
                            fullWidth>
                            {t("ngabuburit_enpower_button")}
                          </Button>
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            ))}
          </div>
          <div
            role="button"
            tabIndex={0}
            className="w-full sm:hidden mt-4 flex items-center justify-center"
            onClick={() => {
              gtmEvent("cs_view_program", {
                program_category: listData[listData.length - 1].title,
              })
            }}>
            <a className="cursor-pointer w-[170px]" href={listData[listData.length - 1].url}>
              <div className="relative flex flex-grow flex-col items-center justify-center rounded-xl bg-neutral-50 p-5 h-[81.2px] sm:h-[200px]">
                <div className="absolute inset-0 rounded-lg z-0">
                  <ImageComponent
                    src={listData[listData.length - 1].image}
                    alt={listData[listData.length - 1].title}
                    rounded
                    roundedSize="extra-large"
                  />
                </div>
                <div className="relative z-10">
                  <Text
                    color="light"
                    alignText="center"
                    size="medium"
                    weight="bold"
                    customClass="leading-[16px] sm:leading[24px] text-[10px] sm:text-[20px]">
                    {listData[listData.length - 1].title}
                  </Text>
                </div>
              </div>
            </a>
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          className="mt-4 sm:mt-8 flex justify-center"
          onClick={() => {
            gtmEvent("cs_view_program", {
              program_category: t("all_categories"),
            })
          }}>
          <Button
            variant="outlined"
            color="primary"
            customClass="w-full sm:w-[384px]"
            href="/programs/all">
            {t("explore_more_topics")}
          </Button>
        </div>
      </ContainerComponent>
    </section>
  )
}

export default Categories
